
<script>
  /* eslint-disable max-len */
  import { onDestroy, onMount } from "svelte";
  import { fly } from "svelte/transition";
  import { media } from "svelte-match-media";
  import Actions from "@components/Actions";
  import Button from "@components/Button";
  import GivenQuestions from "@components/features/GivenQuestions";
  import TextButton from "@components/TextButton";
  import { _ } from "@services/i18n";
  import { authenticatedFetch } from "@stores/auth";
  import {
    displaySuggestedQuestions,
    suggestedQuestions,
    suggestedQuestionsOnScreen,
    suggestedQuestionsSheetOpen,
    suggestedQuestionsTimerCancelled
  } from "@stores/features/suggested-questions";
  import { getConfiguration } from "@stores/component-configurations";
  import { experience, voiceMode } from "@stores/global";
  import { FADE_DURATION } from "../../consts";

  const ANIMATION_DURATION = FADE_DURATION * 2;
  const INACTIVITY_DELAY = 4000;
  let scale = false;
  let animationTimer;
  let inactivityTimer;

  const {
    delay,
    title
  } = getConfiguration(
    "suggested-questions",
    {
      delay: INACTIVITY_DELAY,
      title: $_("components.suggested_questions.title", { default: "Top follow-ups" }),
    }
  );

  let timerCancellationUnsubscribe;

  onMount(() => {
    if (Number(delay)) {
      inactivityTimer = setTimeout(open, Number(delay));

      timerCancellationUnsubscribe = suggestedQuestionsTimerCancelled.subscribe((cancelled) => {
        if (cancelled) {
          clearTimers();
        }
      });
    } else {
      open();
    }
  });

  onDestroy(() => {
    clearTimers();
    suggestedQuestionsSheetOpen.update(() => false);
    if (timerCancellationUnsubscribe) {
      timerCancellationUnsubscribe();
    }
  });

  /**
   * Clean up any current timers
   */
  function clearTimers() {
    if (animationTimer) {
      clearTimeout(animationTimer);
      animationTimer = null;
    }
    if (inactivityTimer) {
      clearTimeout(inactivityTimer);
      inactivityTimer = null;
    }
  }

  /**
   * Shuffle available suggested questions
   *
   * If server returns no results, shuffle existing
   * suggestions
   */
  async function shuffleSuggestions() {
    const suggestions = await authenticatedFetch(
      `/experience/${$experience.id}/suggested-questions`
    );
    suggestedQuestions.update((qs) => {
      if (suggestions.length) {
        return suggestions;
      }

      for (let i = qs.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [qs[i], qs[j]] = [qs[j], qs[i]];
      }

      return qs;
    });
  }

  /**
   * Open suggested questions sheet
   */
  function open() {
    if (Number(delay)) {
      scale = true;
      // eslint-disable-next-line max-nested-callbacks
      animationTimer = setTimeout(() => {
        scale = false;
        suggestedQuestionsSheetOpen.update(() => true);
        clearTimers();
      }, ANIMATION_DURATION);
    } else {
      suggestedQuestionsSheetOpen.update(() => true);
    }
  }

  /**
   * Close suggested questions sheet
   */
  function close() {
    suggestedQuestionsSheetOpen.update(() => false);
  }
</script>

<style>
  .ellipsis {
    background: url("/images/ellipsis.svg") no-repeat center center;
    background-size: 100%;
    bottom: 11rem;
    cursor: pointer;
    display: block;
    height: 6px;
    left: calc(50% - 15px);
    position: absolute;
    transform-origin: center;
    width: 30px;
    z-index: 1;
  }

  .scale {
    animation-duration: .3s;
    animation-iteration-count: 1;
    animation-name: grow;
  }

  .voiceMode {
    bottom: 15rem;
  }

  @keyframes grow {
    0% {
      transform: scale(1);
    }
    50% {
      transform: scale(2);
    }
    100% {
      transform: scale(1);
    }
  }
</style>

{#if $suggestedQuestionsOnScreen}
  {#if $suggestedQuestionsSheetOpen}
    <GivenQuestions
      delay={FADE_DURATION}
      on:error={() => displaySuggestedQuestions.set(true)}
      on:question-asked={() => displaySuggestedQuestions.set(false)}
      questions={$suggestedQuestions}
      {title}
    >
      <div slot="actions">
        <Actions>
          {#if $media.mobile}
            <TextButton bold={true} on:click={shuffleSuggestions}>
              {
                $_("components.suggested_questions.left_action_button.text", {
                  default: "Shuffle"
                })
              }
            </TextButton>
          {:else}
            <Button on:click={shuffleSuggestions}>
              {
                $_("components.suggested_questions.left_action_button.text", {
                  default: "Shuffle"
                })
              }
            </Button>
          {/if}
          <TextButton bold={true} on:click={close}>
            {
              $_("components.suggested_questions.right_action_button.text", {
                default: "Close"
              })
            }
          </TextButton>
        </Actions>
      </div>
    </GivenQuestions>
  {:else if Number(delay)}
    <div class="ellipsis" class:scale class:voiceMode={$voiceMode} on:click={open} transition:fly={{ delay: FADE_DURATION, y: 100 }} />
  {/if}
{/if}
